<template>
  <section>
    <div class="container">
      <filter-bar
        :filters="filters"
        :filter-bar-limit="5"
        class="mb-4"
        close-icon-class="bi bi-x fs-6"
        filter-icon-class="bi bi-filter fs-6"
        search-icon-class="bi bi-search"
      />
      <div class="row g-5">
        <template v-if="events.length > 0">
          <a
            v-for="event in events"
            :key="event.id"
            :href="getDetailUrl(event.id)"
            class="col-12 col-md-6 col-lg-4"
            data-test="event-card"
          >
            <div class="card c-event-card shadow-sm border-0 card-lift overflow-hidden h-100">
              <div class="position-relative">
                <div class="ratio ratio-1x1 c-event-card__image">
                  <div
                    v-if="!event.listing_image_square_url || failedImages.has(event.id)"
                    class="c-event-card__image-placeholder bg-primary-gradient-light d-flex align-items-center justify-content-center"
                  >
                    <i class="bi bi-calendar3 fs-1 text-primary opacity-50" />
                  </div>
                  <div
                    v-else-if="isImageLoading(event)"
                    class="c-event-card__image-placeholder bg-primary-gradient-light o-shimmer position-absolute"
                  />
                  <img
                    v-if="event.listing_image_square_url && !failedImages.has(event.id)"
                    :src="event.listing_image_square_url"
                    :alt="event.title"
                    :class="['object-fit-cover', {'invisible': isImageLoading(event)}]"
                    @load="handleImageLoad(event.id)"
                    @error="handleImageError(event.id)"
                  >
                </div>
                <span
                  v-if="event.is_registration_full && event.is_waitlist_available"
                  class="badge rounded-pill shadow-sm bg-info-subtle text-info-emphasis position-absolute top-0 end-0 m-2"
                  :data-test="`event-${event.id}-waitlist-available-badge`"
                >
                  Waitlist Available
                </span>
                <span
                  v-else-if="event.is_registration_full"
                  class="badge rounded-pill shadow-sm bg-warning-subtle text-warning-emphasis position-absolute top-0 end-0 m-2"
                  :data-test="`event-${event.id}-registration-full-badge`"
                >
                  Registration Full
                </span>
                <span
                  v-else-if="event.is_registration_closed"
                  class="badge rounded-pill shadow-sm bg-secondary-subtle text-secondary-emphasis position-absolute top-0 end-0 m-2"
                  :data-test="`event-${event.id}-registration-closed-badge`"
                >
                  Registration Closed
                </span>
              </div>
              <div class="card-body c-event-card__body">
                <div class="c-event-card__header">
                  <small class="text-uppercase fw-semibold ls-md" :data-test="`event-${event.id}-date-range`">{{ dateString(event) }}</small>
                  <h4 class="my-2 text-primary" :title="event.title">
                    {{ event.title }}
                  </h4>
                </div>
                <div class="c-event-card__summary">
                  <small :title="event.summary">{{ event.summary }}</small>
                </div>
                <div class="mt-auto pt-3">
                  <small>{{ event.site_name }}</small>
                </div>
              </div>
            </div>
          </a>
        </template>
        <div v-else>
          <section class="py-5 bg-light-subtle">
            <div class="container my-lg-7">
              <div class="row justify-content-center text-center">
                <div class="col-lg-6 col-md-9 col-12 d-flex flex-column gap-4">
                  <div>
                    <i class="fs-1 bi bi-calendar-event text-primary" />
                  </div>
                  <div>
                    <h2 class="fs-1">
                      No events found
                    </h2>
                    <p class="mb-0">
                      We're working to bring you new and exciting events. Please check back soon!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <pagination
        class="mt-4"
        :page-data="pageData"
        @load-page="loadPage"
        @change-page-size="changePageSize"
      />
    </div>
  </section>
</template>
<script>
import {mapState} from 'vuex';
import ListViewMixin from '@/js/mixins/listViewMixin.js';
import FilterBar from '@/js/components/filters/FilterBar.vue';
import {humanDateRange} from '@canopyllc/roots/utils/dates.js';
import Pagination from '@/js/components/Pagination.vue';

export default {
  name: 'RegistrationListApp',
  components: {FilterBar, Pagination},
  mixins: [ListViewMixin],
  inject: ['urls', 'api'],
  props: {
    orgSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      failedImages: new Set(),
      loadedImages: new Set(),
    };
  },
  computed: {
    ...mapState({
      events: (state) => state.list.itemList,
      pageData: (state) => state.pagination.pageData,
    }),
  },
  methods: {
    changePageSize(pageSize) {
      this.$store.commit('pagination/setPageSize', {pageSize});
    },
    loadPage(pageNum) {
      this.$store.commit('pagination/setPageNum', {pageNum});
    },
    getDetailUrl(id) {
      return this.urls.eventDetailUrl.replace('1', id);
    },
    dateString(event) {
      return humanDateRange(event.start_at, event.end_at, event.time_zone);
    },
    handleImageLoad(eventId) {
      this.loadedImages.add(eventId);
      this.$forceUpdate();
    },
    handleImageError(eventId) {
      this.failedImages.add(eventId);
      this.$forceUpdate();
    },
    isImageLoading(event) {
      return event.listing_image_square_url
        && !this.loadedImages.has(event.id)
        && !this.failedImages.has(event.id);
    },
  },
};
</script>
